/**
 * The three small pieces the call surface repeats.
 *
 * They live here rather than in `components/ui` because they are all specific
 * to the dark call chrome: a control-bar button, a menu that opens upward from
 * it, and the microphone meter. Nothing else in the app renders on black.
 *
 * The menu is hand-rolled rather than pulling in another Radix package. It
 * needs three behaviours — close on outside click, close on Escape, return
 * focus to the trigger — and all three are visible in forty lines here, which
 * is a better trade than a dependency for one use.
 */

import * as React from 'react'
import { ChevronUp } from 'lucide-react'
import { cn } from '@/lib/utils'

/* -------------------------------------------------------------------------- */
/* control-bar button                                                          */
/* -------------------------------------------------------------------------- */

export type CallButtonTone = 'on' | 'off' | 'active' | 'danger'

const TONES: Record<CallButtonTone, string> = {
  // "On" is the resting state and is therefore the QUIET one. Video products
  // that light up every enabled control leave a bar of glowing buttons in
  // which the one thing that is wrong cannot be found.
  on: 'bg-white/10 text-white hover:bg-white/16 border-white/12',
  // "Off" is the exception and says so — muted mic, camera down.
  off: 'bg-[#f2685f] text-[#2a0f0d] hover:brightness-110 border-transparent',
  active: 'bg-[#a690ff] text-[#150f2b] hover:brightness-110 border-transparent',
  danger: 'bg-[#cf4034] text-white hover:brightness-110 border-transparent',
}

export interface CallButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  tone?: CallButtonTone
  label: string
  /** Renders the label under the icon. Used on wide screens only. */
  showLabel?: boolean
}

export function CallButton({
  tone = 'on',
  label,
  showLabel = false,
  className,
  children,
  ...props
}: CallButtonProps) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={cn(
        'group inline-flex h-11 items-center justify-center gap-2 rounded-full border px-4',
        'text-sm font-medium transition-[background-color,color,transform] duration-150',
        'active:scale-[0.97] disabled:pointer-events-none disabled:opacity-40',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/70',
        '[&_svg]:size-[1.15rem] [&_svg]:shrink-0',
        showLabel ? 'min-w-11' : 'w-11 px-0',
        TONES[tone],
        className,
      )}
      {...props}
    >
      {children}
      {showLabel ? <span className="hidden lg:inline">{label}</span> : null}
    </button>
  )
}

/* -------------------------------------------------------------------------- */
/* menu                                                                        */
/* -------------------------------------------------------------------------- */

/**
 * On a phone, a `Menu` stops being a popover and becomes an action sheet.
 *
 * Pass it as the `Menu`'s `className`. It is not a taste call — the panel is
 * 288px wide and every one of these is anchored to a control somewhere along a
 * 375px row, so a popover that is centred on its trigger (the device menus, a
 * third of the way along the control bar) hangs off the LEFT edge, and one that
 * is left-aligned to it (the camera and microphone pickers under the preview)
 * hangs off the RIGHT. The document does not scroll, so what hangs off is not
 * reachable — it is gone. Narrowing the panel produces a narrow menu that is
 * still in the wrong place.
 *
 * `fixed` is what makes the sheet independent of where its trigger happens to
 * be. The cost is that it is then positioned against the VIEWPORT rather than
 * against whatever safe-area padding an ancestor applied, which is why `--sab`
 * appears in the offset explicitly.
 *
 * The default clears the home indicator and nothing else. Anything with its own
 * chrome at the bottom of the screen overrides the offset by passing a second
 * `max-sm:bottom-*` after this one — `cn` merges them and the later one wins.
 */
export const MENU_SHEET = [
  'max-sm:fixed max-sm:inset-x-3 max-sm:w-auto max-sm:translate-x-0',
  'max-sm:mb-0 max-sm:bottom-[calc(var(--sab)+0.75rem)]',
].join(' ')

export function Menu({
  trigger,
  children,
  align = 'center',
  className,
}: {
  trigger: (props: { open: boolean; toggle: () => void }) => React.ReactNode
  children: (close: () => void) => React.ReactNode
  align?: 'start' | 'center' | 'end'
  className?: string
}) {
  const [open, setOpen] = React.useState(false)
  const wrapper = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (!open) return

    const onPointerDown = (event: PointerEvent) => {
      if (!wrapper.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setOpen(false)
      // Focus goes back where it came from. Without this, Escape leaves the
      // keyboard at the top of the document in the middle of a call.
      wrapper.current?.querySelector('button')?.focus()
    }

    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const alignment =
    align === 'start' ? 'left-0' : align === 'end' ? 'right-0' : 'left-1/2 -translate-x-1/2'

  return (
    <div ref={wrapper} className="relative">
      {trigger({ open, toggle: () => setOpen((value) => !value) })}
      {open ? (
        <div
          role="menu"
          className={cn(
            // `min()` rather than a flat `w-72`: the panel is anchored to a
            // control that can sit anywhere along a 375px bar, and a fixed
            // 288px is wider than the room left beside most of them. The
            // max-height is the same argument vertically — a laptop with a
            // dock, a headset and two webcams produces a device list taller
            // than a landscape phone.
            'absolute bottom-full z-50 mb-3 w-[min(18rem,calc(100vw-1.5rem))] origin-bottom rounded-xl border border-white/12',
            'max-h-[min(24rem,calc(100dvh-8rem))] overflow-y-auto overscroll-contain',
            'bg-[#16130f]/95 p-1.5 shadow-[0_24px_60px_-20px_rgb(0_0_0/0.9)] backdrop-blur-xl',
            'motion-safe:animate-[era-menu_140ms_ease-out]',
            alignment,
            className,
          )}
        >
          {children(() => setOpen(false))}
        </div>
      ) : null}
    </div>
  )
}

/** A chevron that reads as "there is more behind this button". */
export function MenuChevron({ open }: { open: boolean }) {
  return (
    <ChevronUp
      className={cn('size-3.5 transition-transform duration-150', open ? '' : 'rotate-180')}
    />
  )
}

export function MenuLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="px-2.5 pt-2 pb-1.5 text-[0.6875rem] font-semibold tracking-[0.1em] text-white/35 uppercase">
      {children}
    </p>
  )
}

export function MenuItem({
  selected,
  onSelect,
  children,
  icon,
}: {
  selected?: boolean
  onSelect: () => void
  children: React.ReactNode
  icon?: React.ReactNode
}) {
  return (
    <button
      type="button"
      /*
       * A radio ONLY when this row is one of a set with a current choice —
       * which is what `selected` being supplied means.
       *
       * The device pickers are genuinely radios: one output device is current.
       * "Leave" and "End for everyone" are one-shot actions with no current
       * value, and they were announced as "radio menu item" with no checked
       * state, which tells a screen-reader user they are choosing between two
       * settings rather than performing an irreversible action. `menuitem` is
       * the role for a command.
       */
      role={selected === undefined ? 'menuitem' : 'menuitemradio'}
      {...(selected === undefined ? {} : { 'aria-checked': selected })}
      onClick={onSelect}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[0.8125rem]',
        // A 34px row is a miss target on a thumb, and picking the wrong output
        // device mid-call is a loud mistake. 44px on touch, unchanged on a
        // pointer where the list stays as dense as the operator has it.
        'pointer-coarse:min-h-11',
        'transition-colors duration-100 active:bg-white/12',
        selected ? 'bg-white/10 text-white' : 'text-white/65 hover:bg-white/6 hover:text-white',
      )}
    >
      <span className="flex size-4 shrink-0 items-center justify-center">{icon}</span>
      <span className="truncate">{children}</span>
    </button>
  )
}
